import { useState } from 'react';
import {
  Anchor,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  Group,
  Paper,
  Stack,
  Switch,
  Text,
  VisuallyHidden,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconCookie } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';
import { useCookieConsent } from '../context/CookieConsentContext';
import './CookiesBanner.css';

// Preferences form (rendered inside the Drawer)
function PreferencesForm({ initial, onSave, onRejectAll }) {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(Boolean(initial?.analytics));
  const [marketing, setMarketing] = useState(Boolean(initial?.marketing));

  return (
    <Stack gap="lg">
      <Paper withBorder p="md" radius="md" className="cookies-banner-pref-row">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Box style={{ flex: 1 }}>
            <Text id="cookie-pref-essential-title" fw={600} size="sm">
              {t('cookiesBannerPrefEssentialTitle', 'Essential')}
            </Text>
            <Text size="xs" c="dimmed" mt={4} lh={1.5}>
              {t(
                'cookiesBannerPrefEssentialDesc',
                'Required for the site to function (login, cart, security). Always on.'
              )}
            </Text>
          </Box>
          <Switch checked disabled aria-label="Essential cookies" aria-describedby="cookie-pref-essential-title" />
        </Group>
      </Paper>

      <Paper withBorder p="md" radius="md" className="cookies-banner-pref-row">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Box style={{ flex: 1 }}>
            <Text id="cookie-pref-analytics-title" fw={600} size="sm">
              {t('cookiesBannerPrefAnalyticsTitle', 'Analytics')}
            </Text>
            <Text size="xs" c="dimmed" mt={4} lh={1.5}>
              {t(
                'cookiesBannerPrefAnalyticsDesc',
                'Help us understand how visitors interact with the site so we can improve it.'
              )}
            </Text>
          </Box>
          <Switch
            checked={analytics}
            onChange={(e) => setAnalytics(e.currentTarget.checked)}
            aria-label={t('cookiesBannerPrefAnalyticsTitle', 'Analytics')}
            aria-describedby="cookie-pref-analytics-title"
          />
        </Group>
      </Paper>

      <Paper withBorder p="md" radius="md" className="cookies-banner-pref-row">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Box style={{ flex: 1 }}>
            <Text id="cookie-pref-marketing-title" fw={600} size="sm">
              {t('cookiesBannerPrefMarketingTitle', 'Marketing')}
            </Text>
            <Text size="xs" c="dimmed" mt={4} lh={1.5}>
              {t(
                'cookiesBannerPrefMarketingDesc',
                'Used to personalize ads and measure their effectiveness across websites.'
              )}
            </Text>
          </Box>
          <Switch
            checked={marketing}
            onChange={(e) => setMarketing(e.currentTarget.checked)}
            aria-label={t('cookiesBannerPrefMarketingTitle', 'Marketing')}
            aria-describedby="cookie-pref-marketing-title"
          />
        </Group>
      </Paper>

      <Divider />

      <Group justify="space-between" gap="xs" wrap="wrap">
        <Button variant="default" onClick={onRejectAll}>
          {t('cookiesBannerRejectAll', 'Reject all')}
        </Button>
        <Button onClick={() => onSave({ essential: true, analytics, marketing })}>
          {t('cookiesBannerSavePreferences', 'Save preferences')}
        </Button>
      </Group>
    </Stack>
  );
}

// ─── Banner (the bottom-of-screen card) ────────────────────────────────────
function Banner({ onCustomize, onAccept, onReject }) {
  const { t } = useTranslation();
  return (
    <Paper
      className="cookies-banner"
      shadow="md"
      p="md"
      withBorder={false}
      role="region"
      aria-label={t('cookiesBannerTitle', 'This website uses cookies')}
    >
      <VisuallyHidden aria-live="polite">
        {t('cookiesBannerTitle', 'This website uses cookies')}
      </VisuallyHidden>
      <Container size="lg">
        <Group justify="space-between" align="center" gap="md" wrap="wrap">
          <Group
            gap="sm"
            align="flex-start"
            wrap="nowrap"
            style={{ flex: 1, minWidth: 0 }}
          >
            <IconCookie
              size={32}
              stroke={1.5}
              className="cookies-banner-icon"
              aria-hidden="true"
            />
            <Box className="cookies-banner-text">
              <Text fw={600} size="sm" mb={4}>
                {t('cookiesBannerTitle', 'This website uses cookies')}
              </Text>
              <Text size="sm" c="dimmed" lh={1.45}>
                {t(
                  'cookiesBannerDesc',
                  'We use cookies to ensure you get the best experience on our website. You can accept all, reject non-essential cookies, or customize your preferences.'
                )}{' '}
                <Anchor
                  component={Link}
                  to="/privacy"
                  size="sm"
                  fw={500}
                  underline="hover"
                >
                  {t('cookiesBannerLearnMore', 'Learn more')}
                </Anchor>
              </Text>
            </Box>
          </Group>

          <Group gap="xs" wrap="nowrap" className="cookies-banner-actions">
            <Button
              variant="default"
              size="sm"
              onClick={onCustomize}
              className="cookies-banner-btn"
            >
              {t('cookiesBannerCustomize', 'Customize')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onReject}
              className="cookies-banner-btn"
            >
              {t('cookiesBannerRejectAll', 'Reject all')}
            </Button>
            <Button size="sm" onClick={onAccept} className="cookies-banner-btn">
              {t('cookiesBannerAcceptAll', 'Accept all')}
            </Button>
          </Group>
        </Group>
      </Container>
    </Paper>
  );
}

export default function CookiesBanner() {
  const { t } = useTranslation();
  const {
    consent,
    isFullyDecided,
    drawerOpen,
    openPreferences,
    closePreferences,
    acceptAll,
    rejectAll,
    savePreferences,
  } = useCookieConsent();

  const isMobile = useMediaQuery('(max-width: 768px)');

  const showBanner = !isFullyDecided;

  return (
    <>
      {showBanner && (
        <Banner
          onCustomize={openPreferences}
          onAccept={acceptAll}
          onReject={rejectAll}
        />
      )}
      <Drawer
        opened={drawerOpen}
        onClose={closePreferences}
        title={t('cookiesBannerDrawerTitle', 'Cookie preferences')}
        position={isMobile ? 'bottom' : 'right'}
        size={isMobile ? '90%' : 'md'}
        padding="lg"
        zIndex={1200}
      >
        <PreferencesForm
          key={
            isFullyDecided
              ? `${consent.analytics}-${consent.marketing}`
              : 'init'
          }
          initial={consent}
          onSave={savePreferences}
          onRejectAll={rejectAll}
        />
      </Drawer>
    </>
  );
}
